import { unwrapEnvelope } from '@/model/client';
import { useLazyGetApiUsersOauthBySubIdQuery } from '@/model/user/api.generated';
import type { UserResponse } from '@/model/user/dto.generated';
import { logger as SentryLogger } from '@/shared/helpers/logger';
import { useAppDispatch } from '@/shared/hooks/useStore';
import { getErrorStatus } from '@/shared/utils';
import { useAnalyticsTracker } from '@/viewmodel/features/analytics';
import { setStatus, setUser } from '@/viewmodel/features/user/state/userSlice';
import { useCallback, useEffect, useRef } from 'react';

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 8000;

export const toError = (err: unknown): Error =>
  err instanceof Error ? err : new Error(String(err));

function backoffDelay(attempt: number): number {
  return Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
}

export function useFetchUser(
  forceSignOut: () => void,
  onGiveUp: () => void,
): { fetchUser: (sub: string) => Promise<void>; abortFetch: () => void } {
  const dispatch = useAppDispatch();
  const { trackSessionStarted } = useAnalyticsTracker();
  const retryRef = useRef<AbortController | null>(null);
  const [triggerGetUser] = useLazyGetApiUsersOauthBySubIdQuery();

  const fetchUser = useCallback(
    async (sub: string) => {
      const controller = new AbortController();
      retryRef.current?.abort();
      retryRef.current = controller;

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        if (controller.signal.aborted) return;

        try {
          const result = await triggerGetUser({ subId: sub }).unwrap();
          const user = unwrapEnvelope<UserResponse>(result);
          dispatch(setUser(user));
          dispatch(setStatus('ready'));
          void trackSessionStarted('app');
          return;
        } catch (err: unknown) {
          if (controller.signal.aborted) return;

          const status = getErrorStatus(err);

          if (status === 401 || status === 403) {
            SentryLogger.warn('useUserData: auth error fetching user, signing out', {
              sub,
              status,
              log_source: 'useUserData',
            });
            forceSignOut();
            return;
          }

          if (status === 404) {
            SentryLogger.info('useUserData: user not found in DB (not registered yet)', {
              sub,
              log_source: 'useUserData',
            });
            dispatch(setUser(null));
            dispatch(setStatus('not_found'));
            return;
          }

          SentryLogger.error('useUserData: query error', {
            error: toError(err),
            attempt,
            log_source: 'useUserData',
          });

          const isLastAttempt = attempt === MAX_RETRIES - 1;

          if (isLastAttempt) {
            onGiveUp();
            dispatch(setStatus('error'));
            return;
          }

          await new Promise<void>((resolve) => {
            const timer = setTimeout(resolve, backoffDelay(attempt));
            controller.signal.addEventListener('abort', () => {
              clearTimeout(timer);
              resolve();
            });
          });
        }
      }
    },
    [dispatch, triggerGetUser, trackSessionStarted, forceSignOut, onGiveUp],
  );

  const abortFetch = useCallback(() => retryRef.current?.abort(), []);

  useEffect(() => abortFetch, [abortFetch]);

  return { fetchUser, abortFetch };
}
